// src/pages/school/fees/FeesDashboard.js
import React, { useEffect, useMemo, useState } from "react";
import { Container, Row, Col, Card, Button, ButtonGroup } from "react-bootstrap";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { collection, getDocs, orderBy, query } from "firebase/firestore";

import useSchool from "../../../hooks/useSchool";
import { useAuth } from "../../../context/AuthContext";
import RequireFeesUnlock from "./components/RequireFeesUnlock";
import { clearFeeAuth } from "./data/feesAuth";
import { db } from "../../../services/firebase";

// Recharts
import {
  ResponsiveContainer,
  PieChart, Pie, Tooltip as RTooltip, Legend,
  BarChart, Bar, CartesianGrid, XAxis, YAxis
} from "recharts";

/* ------------ Firestore refs ------------ */
const feesPaymentsRef = (sid) => collection(db, "schools", sid, "fees_payments");

/* ------------ utils ------------ */
const INR = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0
  }).format(n || 0);

const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

/* Small KPI card */
function KPICard({ label, value, sub }) {
  return (
    <Card className="border-0 shadow-sm h-100">
      <Card.Body>
        <div className="text-muted small">{label}</div>
        <div className="h4 mb-0">{value}</div>
        {sub ? <div className="small text-muted mt-1">{sub}</div> : null}
      </Card.Body>
    </Card>
  );
}

export default function FeesDashboard() {
  const { school } = useSchool();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const schoolId = school?.id || null;
  const uid = user?.uid || null;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // We show Overview KPIs/charts only on the root "/school/fees"
  const onRoot = pathname === "/school/fees";

  /* ------------ Load payments (overview only) ------------ */
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (!schoolId) { if (mounted) setLoading(false); return; }
        const q = query(feesPaymentsRef(schoolId), orderBy("createdAt", "desc"));
        const snap = await getDocs(q);
        if (!mounted) return;
        setRows(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error("FeesDashboard: failed to load payments", e);
        setRows([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [schoolId]);

  /* ------------ KPIs / Charts data ------------ */
  const collected = useMemo(
    () => rows.reduce((s, r) => s + (Number(r.amount) || 0), 0),
    [rows]
  );
  const count = rows.length;
  const avg = count ? collected / count : 0;

  const byCat = useMemo(() => {
    const m = new Map();
    rows.forEach(r => {
      const name = r.categoryName || r.categoryId || "Uncategorized";
      m.set(name, (m.get(name) || 0) + (Number(r.amount) || 0));
    });
    return Array.from(m.entries()).map(([name, value]) => ({ name, value }));
  }, [rows]);

  const byMonth = useMemo(() => {
    const m = new Map();
    rows.forEach(r => {
      const dt = r.createdAt?.toDate ? r.createdAt.toDate() : null;
      if (!dt) return;
      const k = monthKey(dt);
      m.set(k, (m.get(k) || 0) + (Number(r.amount) || 0));
    });
    return Array.from(m.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, amount]) => ({ month, amount }));
  }, [rows]);

  /* ------------ Lock (clear PIN) ------------ */
  const onLock = () => {
    if (!uid || !schoolId) return;
    clearFeeAuth(schoolId, uid);
    navigate("/school/fees/login?next=/school/fees", { replace: true });
  };

  return (
    <RequireFeesUnlock>
      <Container className="py-4">
        {/* Header + actions */}
        <Row className="align-items-center mb-3">
          <Col>
            <h5 className="mb-0">Fees</h5>
            <div className="text-muted small">{school?.name || "School"}</div>
          </Col>
          <Col xs="auto" className="d-flex flex-wrap gap-2">
            {/* Use router links so the app doesn't full-reload */}
            <Button as={NavLink} to="fix" variant="primary">
              Set Fee
            </Button>
            <Button as={NavLink} to="collect" variant="primary">
              Collect Fees
            </Button>
            <Button as={NavLink} to="dues" variant="outline-primary">
              Dues
            </Button>
            <Button as={NavLink} to="payments" variant="outline-primary">
              Payments
            </Button>
            <Button as={NavLink} to="reports" variant="outline-secondary">
              Reports
            </Button>
            <Button as={NavLink} to="settings" variant="outline-secondary">
              Settings
            </Button>
            <Button variant="outline-danger" onClick={onLock} title="Lock Fees on this device">
              Lock
            </Button>
          </Col>
        </Row>

        {/* Overview KPIs + Charts only on /school/fees */}
        {onRoot && (
          <>
            {/* KPIs */}
            <Row className="g-3 mb-3">
              <Col md={3}>
                <KPICard label="Collected" value={loading ? "—" : INR(collected)} />
              </Col>
              <Col md={3}>
                <KPICard label="Payments" value={loading ? "—" : count} />
              </Col>
              <Col md={3}>
                <KPICard label="Avg. Ticket" value={loading ? "—" : INR(avg)} />
              </Col>
              <Col md={3}>
                <KPICard label="Categories" value={loading ? "—" : byCat.length} />
              </Col>
            </Row>

            {/* Charts */}
            <Row className="g-3">
              <Col md={6}>
                <Card className="border-0 shadow-sm h-100">
                  <Card.Header className="bg-white border-0 fw-semibold">Category Split</Card.Header>
                  <Card.Body style={{ height: 320 }}>
                    {loading ? (
                      <div className="text-muted text-center pt-5">Loading…</div>
                    ) : byCat.length === 0 ? (
                      <div className="text-muted text-center pt-5">No data</div>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={byCat} dataKey="value" nameKey="name" outerRadius={110} label />
                          <RTooltip formatter={(v) => INR(v)} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              <Col md={6}>
                <Card className="border-0 shadow-sm h-100">
                  <Card.Header className="bg-white border-0 fw-semibold">Monthly Trend</Card.Header>
                  <Card.Body style={{ height: 320 }}>
                    {loading ? (
                      <div className="text-muted text-center pt-5">Loading…</div>
                    ) : byMonth.length === 0 ? (
                      <div className="text-muted text-center pt-5">No data</div>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={byMonth}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis />
                          <RTooltip formatter={(v) => INR(v)} />
                          <Legend />
                          <Bar dataKey="amount" name="Collected" />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        )}

        {/* Child pages render here (collect / dues / payments / fix / reports / settings) */}
        <div className="mt-3">
          <Outlet />
        </div>
      </Container>
    </RequireFeesUnlock>
  );
}
